import { BorderCellEntry, BorderInfoEntry, BorderRangeEntry, BorderSideValue } from './types';

const sideKey = (s?: BorderSideValue): string => (s ? `${s.style}|${s.color}` : '');

/**
 * A cell qualifies for rectangle aggregation when all four sides are present
 * with one identical style+color — the "full grid" styling that dominates
 * real financial workbooks. Returns the shared signature, or null.
 */
const uniformSignature = (value: BorderCellEntry['value']): string | null => {
    const { l, r, t, b } = value;
    if (!l || !r || !t || !b) return null;
    const key = sideKey(l);
    return sideKey(r) === key && sideKey(t) === key && sideKey(b) === key ? key : null;
};

type Run = { c1: number; c2: number };

/** Merge one signature-group of cells into maximal row-banded rectangles. */
const cellsToRects = (cells: { r: number; c: number }[]): { r1: number; r2: number; c1: number; c2: number }[] => {
    // Row → sorted column runs.
    const runsByRow = new Map<number, Run[]>();
    const byRow = new Map<number, number[]>();
    cells.forEach(({ r, c }) => {
        const cols = byRow.get(r) ?? [];
        cols.push(c);
        byRow.set(r, cols);
    });
    byRow.forEach((cols, r) => {
        cols.sort((a, b) => a - b);
        const runs: Run[] = [];
        cols.forEach((c) => {
            const last = runs[runs.length - 1];
            if (last && c === last.c2 + 1) last.c2 = c;
            else runs.push({ c1: c, c2: c });
        });
        runsByRow.set(r, runs);
    });

    // Extend identical runs down consecutive rows into rectangles.
    const rects: { r1: number; r2: number; c1: number; c2: number }[] = [];
    const open = new Map<string, { r1: number; r2: number; c1: number; c2: number }>();
    const rows = [...runsByRow.keys()].sort((a, b) => a - b);
    rows.forEach((r) => {
        const next = new Map<string, { r1: number; r2: number; c1: number; c2: number }>();
        runsByRow.get(r)!.forEach((run) => {
            const key = `${run.c1}_${run.c2}`;
            const prev = open.get(key);
            if (prev && prev.r2 === r - 1) {
                prev.r2 = r;
                next.set(key, prev);
            } else {
                next.set(key, { r1: r, r2: r, c1: run.c1, c2: run.c2 });
            }
        });
        // Rectangles that did not continue into this row are final.
        open.forEach((rect, key) => {
            if (!next.has(key) || next.get(key) !== rect) rects.push(rect);
        });
        open.clear();
        next.forEach((rect, key) => open.set(key, rect));
    });
    open.forEach((rect) => rects.push(rect));
    return rects;
};

/**
 * Collapse per-cell border entries for rendering, favouring consistency:
 *
 * - Cells with an identical border on all four sides collapse into lossless
 *   'border-all' range rectangles (Fortune-Sheet paints a full box on every
 *   cell in the range). This is the one aggregation that matters — full-grid
 *   tables become a handful of ranges instead of thousands of cell entries.
 * - Every other cell is emitted WHOLE, with all of its sides intact.
 *
 * Deliberately NOT done: splitting a cell's individual sides across per-side
 * ranges (it left cells half-bordered when only some sides survived), and
 * capping/dropping entries (it dropped borders at random on dense sheets).
 * Fortune-Sheet clips per-cell entries to the visible range on the scroll
 * render path (getBorderInfoComputeRange), so a large per-cell list stays
 * smooth to scroll; correctness is never traded for a fixed entry budget.
 */
export const aggregateBorderInfo = (entries: BorderCellEntry[]): BorderInfoEntry[] => {
    const bySignature = new Map<string, { r: number; c: number }[]>();
    const residual: BorderCellEntry[] = [];

    entries.forEach((entry) => {
        const sig = uniformSignature(entry.value);
        if (sig == null) {
            residual.push(entry);
            return;
        }
        const group = bySignature.get(sig) ?? [];
        group.push({ r: entry.value.row_index, c: entry.value.col_index });
        bySignature.set(sig, group);
    });

    const ranges: BorderRangeEntry[] = [];
    bySignature.forEach((cells, sig) => {
        const [styleStr, color] = sig.split('|');
        const style = Number(styleStr);
        cellsToRects(cells).forEach(({ r1, r2, c1, c2 }) => {
            if (r1 === r2 && c1 === c2) {
                // A lone uniform cell gains nothing as a range — keep it as a cell.
                const s = { style, color };
                residual.push({ rangeType: 'cell', value: { row_index: r1, col_index: c1, l: s, r: s, t: s, b: s } });
                return;
            }
            ranges.push({ rangeType: 'range', borderType: 'border-all', style, color, range: [{ row: [r1, r2], column: [c1, c2] }] });
        });
    });

    return [...ranges, ...residual];
};
